// Thin fetch wrapper. Paths like "/api/profile" are proxied to :4242 in dev
// and same-origin in prod. All responses are JSON unless method='raw'.

import type { ApiError } from './types';

export class ApiClientError extends Error {
  code?: string;
  status: number;
  details?: unknown;
  constructor(status: number, body: ApiError | string) {
    const msg = typeof body === 'string' ? body : body.error || 'API error';
    super(msg);
    this.name = 'ApiClientError';
    this.status = status;
    if (typeof body === 'object') {
      this.code = body.code;
      this.details = body.details;
    }
  }
}

// In multi-tenant mode the server gates every /api/* behind a session cookie.
// If the cookie is missing or stale (token rotated by login/logout/reset,
// server restart wiped the in-memory index, etc.) the API returns 401.
// Bounce to /login so the user sees the form instead of a blank state.
//
// Pages where a 401 must NOT hard-navigate. Two kinds: the auth-flow forms,
// whose 401s are in-band errors the form itself displays; and the public
// pages an anonymous visitor is entitled to be on (/board) — bouncing there
// would throw a prospective user out of the product's front door over a
// stray 401 (e.g. a residual query after logging out in another tab).
const NO_BOUNCE_PATHS = new Set(['/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/board']);
// The board's public detail pages (/jobs/123) are parameterized — prefix, not set.
const NO_BOUNCE_PREFIXES = ['/jobs/'];
// window.location.pathname carries the SPA's /app basename (BrowserRouter
// basename in main.tsx, vite base in prod), so the real path is /app/login —
// while the set holds bare route paths. Strip the prefix before comparing;
// without this the set never matches and every entry in it is dead.
function spaPath(): string {
  return window.location.pathname.replace(/^\/app(?=\/|$)/, '') || '/';
}
function maybeBounceOn401(res: Response): boolean {
  if (res.status !== 401) return false;
  if (typeof window === 'undefined') return false;
  const p = spaPath();
  if (NO_BOUNCE_PATHS.has(p)) return false;
  if (NO_BOUNCE_PREFIXES.some((pre) => p.startsWith(pre))) return false;
  window.location.replace('/login');
  return true;
}

async function parseResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    if (maybeBounceOn401(res)) {
      // Throw a sentinel so the awaiting code path doesn't try to render
      // garbage between this fetch returning and the navigation completing.
      throw new ApiClientError(401, 'session_expired');
    }
    let body: ApiError | string;
    try {
      body = await res.json();
    } catch {
      body = await res.text();
    }
    throw new ApiClientError(res.status, body as ApiError | string);
  }
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return res.json() as Promise<T>;
  return res.text() as unknown as Promise<T>;
}

// Sign out: server rotates the user's token (kills all device cookies) and
// clears the local cookie. Hard-nav to /login so the SPA's stale React state
// doesn't bleed through.
export async function logoutSession(): Promise<void> {
  try { await fetch('/api/logout', { method: 'POST' }); } catch { /* network down — proceed */ }
  if (typeof window !== 'undefined') {
    window.location.replace('/login');
  }
}

export async function apiGet<T>(path: string, params?: Record<string, string | number | boolean | undefined>): Promise<T> {
  const url = new URL(path, window.location.origin);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
  }
  const res = await fetch(url.toString());
  return parseResponse<T>(res);
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  return parseResponse<T>(res);
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return parseResponse<T>(res);
}

export async function apiPut<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return parseResponse<T>(res);
}

export async function apiDelete<T>(path: string): Promise<T> {
  const res = await fetch(path, { method: 'DELETE' });
  return parseResponse<T>(res);
}

export async function apiUpload<T>(path: string, formData: FormData): Promise<T> {
  const res = await fetch(path, { method: 'POST', body: formData });
  return parseResponse<T>(res);
}
